import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { toast } from "sonner";

import FormInput from "@/components/form-fields/form-input";
import FormDialog from "@/components/reusables/FormDialog";
import DateInput from "@/components/reusables/custom-form-fields/DateInput";
import { FormSelectSingle } from "@/components/reusables/custom-form-fields/FormSelect";
import InputField from "@/components/reusables/custom-form-fields/InputField";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form } from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import * as Table from "@/components/ui/table";
import {
  EmployeeSuccessMessage,
  Evaluation,
  EvaluationField,
  evaluationFields,
  rehireRecommendation,
  terminationReasons,
} from "@/constants/employees/employee.constants";
import { IEmployee } from "@/interfaces/employee.interface";
import { useToggleEmployeeStatusWsMutation } from "@/redux/api/employeeQuery";
import {
  TEmployeeTermination,
  employeeTermination,
} from "@/schemas/employee-schema";
import { validateResponse } from "@/utils/helper-functions/form.helper";

const TerminateFormModal = ({
  isOpen,
  toggleModal,
}: {
  isOpen: boolean;
  toggleModal: () => void;
}): React.JSX.Element => {
  const terminatingEmployee: IEmployee = JSON.parse(
    localStorage.getItem("terminatingUserDetails") || "{}",
  );

  const employeeName = `${terminatingEmployee?.user?.firstName} ${terminatingEmployee?.user?.lastName}`;

  const form = useForm<TEmployeeTermination>({
    resolver: zodResolver(employeeTermination),
    defaultValues: {
      terminationReason: "",
      employeeEvaluation: {
        attendance: "",
        cooperation: "",
        initiative: "",
        jobKnowledge: "",
        workQuality: "",
      },
      action: "",
      rehireRecommendation: "",
      employeeNotice: {
        givenStatus: "",
        days: undefined,
      },
      severancePay: "",
      additionalComment: "",
    },
  });

  const [isOther, setIsOther] = useState<boolean>(false);

  const {
    watch,
    formState: { errors, dirtyFields },
    reset,
  } = form;

  const handleExit = (): void => {
    toggleModal();
    reset();
    localStorage.removeItem("terminatingUserDetails");
  };

  const terminationReason = watch("terminationReason");
  const [toggleUserStatus] = useToggleEmployeeStatusWsMutation();

  const onSubmit: SubmitHandler<TEmployeeTermination> = async (data) => {
    if (terminationReason === "Other" && data.otherReason) {
      data.terminationReason = data.otherReason;
    }
    const userId = terminatingEmployee?.id;
    const res = await toggleUserStatus({
      userId,
      status: "terminate",
      data,
    });
    validateResponse({
      response: res as MutationResponse,
      onSuccess: () => {
        toast.success(EmployeeSuccessMessage.TERMINATED);
        handleExit();
      },
    });
  };

  const getCheckedValue = (value: EvaluationField, eva: string): boolean => {
    const field = evaluationFields[value];

    return watch(`employeeEvaluation.${field}`) === eva;
  };

  const handleCheckBoxChange = (
    checked: boolean,
    eva: string,
    value: EvaluationField,
  ): void => {
    const field = evaluationFields[value];
    form.setValue(`employeeEvaluation.${field}` as const, checked ? eva : "");
  };

  useEffect(() => {
    const handleTermination = (): void => {
      if (terminationReason === "Other") {
        setIsOther(true);
      } else {
        setIsOther(false);
      }
    };

    handleTermination();
  }, [terminationReason]);

  return (
    <FormDialog
      isOpen={isOpen}
      toggleModal={handleExit}
      title='Termination Assessment'
      isFormDirty={Object.keys(dirtyFields).length > 0}
      className='sm:max-w-lg md:max-w-5xl'
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-8'>
          <div className='grid grid-cols-1 gap-4 mt-4 text-xs lg:grid-cols-2'>
            <InputField label='Employee Name' value={employeeName} disabled />

            <FormInput
              form={form}
              name='terminationDate'
              label='Date of Termination'
              required
              render={(field) => (
                <DateInput
                  {...field}
                  onChange={(date) => {
                    field.onChange(date);
                  }}
                />
              )}
            />

            <InputField
              label='Position'
              value={terminatingEmployee?.user?.role}
              disabled
            />

            <FormInput
              form={form}
              name='terminationReason'
              label='Reason of termination'
              required
              render={(field) => (
                <FormSelectSingle
                  placeholder='Select reason of termination'
                  field={field}
                  options={terminationReasons}
                />
              )}
            />

            {isOther && (
              <FormInput
                form={form}
                name='otherReason'
                label='Reason'
                className='col-span-2'
                render={(field) => (
                  <InputField
                    {...field}
                    type='textarea'
                    placeholder='Please enter reason'
                    onChange={(e: unknown) => {
                      const evt = e as React.ChangeEvent<HTMLInputElement>;
                      field.onChange(evt.target.value);
                    }}
                  />
                )}
              />
            )}
            <div className='col-span-2'>
              <Label className='mb-2 font-normal' required>
                Employee Evaluation
                {errors.employeeEvaluation && (
                  <span className='ml-4 text-xs text-red-500'>
                    {errors.employeeEvaluation.message}
                  </span>
                )}
              </Label>

              <Table.Table>
                <Table.TableHeader>
                  <Table.TableRow className='border rounded-md'>
                    <Table.TableHead></Table.TableHead>
                    {Evaluation.map((item, index) => (
                      <Table.TableHead
                        className='text-center text-primary'
                        key={index}
                      >
                        {item}
                      </Table.TableHead>
                    ))}
                  </Table.TableRow>
                </Table.TableHeader>
                <Table.TableBody className='border'>
                  {Object.keys(evaluationFields).map((item, index) => (
                    <Table.TableRow key={index} className='border'>
                      <Table.TableCell>{item}</Table.TableCell>
                      {Evaluation.map((eva) => (
                        <Table.TableCell className='text-center' key={eva}>
                          <Checkbox
                            checked={getCheckedValue(
                              item as EvaluationField,
                              eva,
                            )}
                            onCheckedChange={(checked) => {
                              handleCheckBoxChange(
                                checked as boolean,
                                eva,
                                item as EvaluationField,
                              );
                            }}
                          />
                        </Table.TableCell>
                      ))}
                    </Table.TableRow>
                  ))}
                </Table.TableBody>
              </Table.Table>
            </div>

            <div className='flex col-span-2 gap-8 '>
              <div className='w-4/6 space-y-4'>
                <Label required>
                  Rehire Recommendations
                  {errors.rehireRecommendation && (
                    <span className='ml-4 text-xs text-red-500'>
                      {errors.rehireRecommendation.message}
                    </span>
                  )}
                </Label>
                <div className='flex items-center justify-around gap-6 ml-2'>
                  {rehireRecommendation.map((item) => (
                    <div
                      className='flex items-center justify-between space-x-2'
                      key={item}
                    >
                      <Label className='font-normal'>{item}</Label>

                      <Checkbox
                        checked={watch("rehireRecommendation") === item}
                        onCheckedChange={() =>
                          form.setValue("rehireRecommendation", item)
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className='space-y-4'>
                <Label required>Severance Pay</Label>
                <div className='flex items-center justify-around gap-6 ml-2'>
                  <div className='flex space-x-2'>
                    <Label className='font-normal'>Yes</Label>
                    <Checkbox
                      checked={watch("severancePay") === "YES"}
                      onCheckedChange={() =>
                        form.setValue("severancePay", "YES")
                      }
                    />
                  </div>
                  <div className='flex items-center justify-between space-x-2'>
                    <div className='flex space-x-2'>
                      <Label className='font-normal'>No</Label>
                      <Checkbox
                        checked={watch("severancePay") === "NO"}
                        onCheckedChange={() =>
                          form.setValue("severancePay", "NO")
                        }
                      />
                    </div>
                  </div>
                </div>
                {errors.severancePay && (
                  <span className='ml-4 text-xs text-red-500'>
                    {errors.severancePay.message}
                  </span>
                )}
              </div>
            </div>
            <div className='flex flex-col col-span-2 mt-2'>
              <div className='grid items-center grid-cols-3 gap-8'>
                <div className='flex flex-col items-center justify-around w-1/2 gap-3'>
                  <Label className='cols-span-2 whitespace-nowrap' required>
                    Employee Notice
                  </Label>

                  <div className='flex gap-8'>
                    <div className='flex items-center space-x-2'>
                      <Label className='font-normal'>Yes</Label>
                      <Checkbox
                        checked={watch("employeeNotice.givenStatus") === "YES"}
                        onCheckedChange={() => {
                          form.setValue("employeeNotice.givenStatus", "YES");
                        }}
                      />
                    </div>
                    <div className='flex items-center space-x-2'>
                      <Label className='font-normal'>No</Label>
                      <Checkbox
                        checked={watch("employeeNotice.givenStatus") === "NO"}
                        onCheckedChange={() => {
                          form.setValue("employeeNotice.givenStatus", "NO");
                        }}
                      />
                    </div>
                  </div>
                  {errors.employeeNotice?.givenStatus && (
                    <span className='ml-4 text-xs text-red-500'>
                      {errors.employeeNotice?.givenStatus.message}
                    </span>
                  )}
                </div>

                <FormInput
                  form={form}
                  name='employeeNotice.days'
                  label='Notice Period'
                  required
                  input={{
                    type: "number",
                    placeholder: "Please enter days",
                  }}
                />
                <FormInput
                  form={form}
                  name='employeeNotice.date'
                  label='Date'
                  required
                  render={(field) => (
                    <DateInput
                      {...field}
                      onChange={(date) => {
                        field.onChange(date);
                      }}
                      // dateLimit={watch("terminationDate") as Date | undefined}
                      validateDate={(date) =>
                        date > (watch("terminationDate") as Date)
                      }
                    />
                  )}
                />
              </div>
            </div>

            <FormInput
              form={form}
              name='additionalComment'
              label='Additional Comment'
              className='col-span-2'
              render={(field) => (
                <InputField
                  {...field}
                  type='textarea'
                  placeholder='Please enter additional comment .....'
                  onChange={(e: unknown) => {
                    const evt = e as React.ChangeEvent<HTMLInputElement>;
                    field.onChange(evt.target.value);
                  }}
                />
              )}
            />
          </div>

          <div className='flex justify-end'>
            <Button className='rounded-sm ' size={"lg"} type='submit'>
              Submit
            </Button>
          </div>
        </form>
      </Form>
    </FormDialog>
  );
};

export default TerminateFormModal;
